import { useId } from "react";
import type { CSSProperties } from "react";
import { Button } from "../../design-system/components";
import "./FinanceCharts.css";

export interface CategoryChartItem {
  key: string;
  label: string;
  amount: number;
  color: string;
}

export interface MoneyTrendItem {
  label: string;
  income: number;
  expense: number;
  debt: number;
}

export interface BalanceChartItem {
  id: string;
  label: string;
  amount: number;
}

const money = (amount: number): string => "₱" + amount.toLocaleString("en-PH", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const nonnegative = (amount: number): number => Number.isFinite(amount) ? Math.max(0, amount) : 0;
const percentage = (amount: number, total: number): string => total > 0 ? `${(amount / total * 100).toFixed(1)}%` : "0.0%";
const compactMoney = (amount: number): string => {
  if (amount >= 1_000_000) return `₱${(amount / 1_000_000).toLocaleString("en-PH", { maximumFractionDigits: 1 })}m`;
  if (amount >= 1_000) return `₱${(amount / 1_000).toLocaleString("en-PH", { maximumFractionDigits: 1 })}k`;
  return `₱${amount.toLocaleString("en-PH", { maximumFractionDigits: 0 })}`;
};

export function CategoryDonut({ items, total, selected, onSelect }: {
  items: CategoryChartItem[];
  total: number;
  selected: string | null;
  onSelect: (key: string | null) => void;
}) {
  const descriptionId = `finance-category-description-${useId()}`;
  const categories = items.map((item) => ({ ...item, amount: nonnegative(item.amount) }))
    .filter((item) => item.amount > 0)
    .sort((a, b) => b.amount - a.amount);
  const sum = categories.reduce((amount, item) => amount + item.amount, 0);
  const spending = nonnegative(total) || sum;
  const active = categories.find((item) => item.key === selected);
  const main = categories.length > 6 ? categories.slice(0, 5) : categories;
  const remainder = categories.length > 6 ? categories.slice(5) : [];
  const segments = main.map((item) => ({
    ...item,
    selected: !selected || selected === item.key,
  }));
  if (remainder.length) {
    segments.push({
      key: "__remaining_categories__",
      label: `${remainder.length} remaining categories`,
      amount: remainder.reduce((amount, item) => amount + item.amount, 0),
      color: "var(--faint)",
      selected: !selected || remainder.some((item) => item.key === selected),
    });
  }
  let offset = 0;

  if (!categories.length) return <p className="finance-chart-empty">No spending recorded for this period. Your category chart will appear when there are expenses.</p>;

  return (
    <div className="finance-category-chart">
      <div className="finance-donut-readout">
        <svg className="finance-donut" viewBox="0 0 240 240" role="group" aria-label="Spending by category. Choose a chart segment or category below to see its transactions.">
          <circle className="finance-donut-track" cx="120" cy="120" r="92" fill="none" strokeWidth="28" aria-hidden="true" />
          {segments.map((item) => {
            const share = sum > 0 ? item.amount / sum * 100 : 0;
            const start = offset;
            offset += share;
            const grouped = item.key === "__remaining_categories__";
            const description = `${item.label}: ${money(item.amount)} (${percentage(item.amount, spending)})`;
            const selectCategory = () => onSelect(selected === item.key ? null : item.key);
            return <circle key={item.key} className={`finance-donut-segment${grouped ? "" : " finance-donut-segment--interactive"}${item.selected ? "" : " finance-donut-segment--muted"}`} cx="120" cy="120" r="92" fill="none" stroke={item.color} strokeWidth="28" pathLength="100" strokeDasharray={`${share} ${100 - share}`} strokeDashoffset={-start} transform="rotate(-90 120 120)" role={grouped ? "img" : "button"} tabIndex={grouped ? undefined : 0} aria-label={grouped ? `${description}. Choose individual categories from the list below.` : description} aria-pressed={grouped ? undefined : selected === item.key} onClick={grouped ? undefined : selectCategory} onKeyDown={grouped ? undefined : (event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                if (!event.repeat) selectCategory();
              }
            }}><title>{grouped ? `${description}. Choose individual categories from the list below.` : description}</title></circle>;
          })}
        </svg>
        <div className="finance-donut-center">
          <span className="finance-chart-eyebrow">{active ? active.label : "Total spending"}</span>
          <strong className={`finance-chart-number${money(active?.amount ?? spending).length > 12 ? " finance-chart-number--long" : ""}`}>{money(active?.amount ?? spending)}</strong>
          <span className="finance-chart-note">{active ? `${percentage(active.amount, spending)} of spending` : `${categories.length} ${categories.length === 1 ? "category" : "categories"}`}</span>
        </div>
      </div>
      <div className="finance-category-key">
        <p id={descriptionId} className="finance-chart-note">Choose a category to see its transactions.</p>
        <ul className={`finance-category-legend${categories.length > 8 ? " finance-category-legend--long" : ""}`} aria-label="Spending by category">
          {categories.map((item) => <li key={item.key}>
            <Button className="finance-category-choice" size="compact" variant="secondary" aria-pressed={selected === item.key} aria-describedby={descriptionId} onClick={() => onSelect(selected === item.key ? null : item.key)}>
              <span className="finance-category-swatch" style={{ "--category-color": item.color } as CSSProperties} aria-hidden="true" />
              <span className="finance-category-name">{item.label}</span>
              <span className="finance-category-values"><strong>{money(item.amount)}</strong><span>{percentage(item.amount, spending)}{selected === item.key ? " · Selected" : ""}</span></span>
            </Button>
          </li>)}
        </ul>
        {remainder.length > 0 && <p className="finance-chart-note">The smallest {remainder.length} categories share one chart segment. Every category is listed here.</p>}
        {selected && <Button className="finance-category-clear" size="compact" variant="quiet" onClick={() => onSelect(null)}>Show all categories</Button>}
      </div>
    </div>
  );
}

const SERIES = [
  { key: "income", label: "Income", className: "finance-series-income" },
  { key: "expense", label: "Spending", className: "finance-series-expense" },
  { key: "debt", label: "Debt payments", className: "finance-series-debt" },
] as const;

export function MoneyTrend({ items }: { items: MoneyTrendItem[] }) {
  const descriptionId = `finance-trend-description-${useId()}`;
  const rows = items.map((item) => ({ label: item.label, income: nonnegative(item.income), expense: nonnegative(item.expense), debt: nonnegative(item.debt) }));
  const max = Math.max(0, ...rows.flatMap((item) => [item.income, item.expense, item.debt]));
  const scale = max > 0 ? max * 1.1 : 1;
  const ticks = [scale, scale * .75, scale * .5, scale * .25, 0];
  const dense = rows.length > 12;
  const labelStep = dense ? Math.ceil((rows.length - 1) / 5) : 1;

  if (!rows.length) return <p className="finance-chart-empty">No dated transactions yet. Income, spending, and debt payments will appear here as they are recorded.</p>;

  return (
    <figure className="finance-trend">
      <ul className="finance-series-legend" aria-label="Chart series">{SERIES.map((series) => <li key={series.key}><span className={`finance-series-swatch ${series.className}`} aria-hidden="true" />{series.label}</li>)}</ul>
      {max > 0 && <div className="finance-trend-graphic" role="img" aria-label={`Income, spending, and debt payments across ${rows.length} periods. Exact amounts are available in View chart values.`} aria-describedby={descriptionId}>
        <div className="finance-trend-axis" aria-hidden="true">{ticks.map((amount, index) => <span key={index} title={money(amount)}>{compactMoney(amount)}</span>)}</div>
        <div className={`finance-trend-plot${dense ? " finance-trend-plot--dense" : ""}`} aria-hidden="true" style={{ "--period-count": rows.length } as CSSProperties}>
          <div className="finance-trend-grid">{ticks.map((_, index) => <span key={index} />)}</div>
          {rows.map((item, index) => <div key={`${item.label}-${index}`} className="finance-trend-period">
            <div className="finance-trend-bars">{SERIES.map((series) => <div key={series.key} className={`finance-trend-bar ${series.className}${item[series.key] === 0 ? " finance-trend-bar--zero" : ""}`} style={{ "--bar-height": `${item[series.key] / scale * 100}%` } as CSSProperties} title={`${item.label} · ${series.label}: ${money(item[series.key])}`} />)}</div>
            <span className="finance-trend-label" title={item.label}>{index % labelStep === 0 || index === rows.length - 1 ? item.label : ""}</span>
          </div>)}
        </div>
      </div>}
      <figcaption id={descriptionId} className="finance-chart-note">{max > 0 ? "Each period compares recorded income, spending, and debt payments. Transfers between your accounts are excluded." : "No money movement recorded for these periods."}</figcaption>
      <details className="finance-chart-details">
        <summary>View chart values</summary>
        <div className="finance-chart-table-wrap">
          <table className="finance-chart-table">
            <caption className="ds-sr-only">Exact income, spending, and debt payments for each period, in Philippine pesos</caption>
            <thead><tr><th scope="col">Period</th>{SERIES.map((series) => <th key={series.key} scope="col">{series.label}</th>)}</tr></thead>
            <tbody>{rows.map((item, index) => <tr key={`${item.label}-${index}`}><th scope="row">{item.label}</th>{SERIES.map((series) => <td key={series.key}>{money(item[series.key])}</td>)}</tr>)}</tbody>
          </table>
        </div>
      </details>
    </figure>
  );
}

export function BalanceBars({ items, label = "Current account balances", note = "Bar lengths compare account balances by size." }: { items: BalanceChartItem[]; label?: string; note?: string }) {
  const accounts = items.map((item) => ({ ...item, amount: Number.isFinite(item.amount) ? item.amount : 0 })).sort((a, b) => b.amount - a.amount);
  const max = Math.max(0, ...accounts.map((item) => Math.abs(item.amount)));
  if (!accounts.length) return <p className="finance-chart-empty">No account balances recorded yet.</p>;

  return (
    <div className="finance-balances-chart">
      <ul className="finance-balance-list" aria-label={label}>{accounts.map((item) => <li key={item.id} className="finance-balance-row">
        <div className="finance-balance-readout"><span>{item.label}</span><strong className="finance-chart-number">{money(item.amount)}</strong></div>
        <div className="finance-balance-track" aria-hidden="true"><span className="finance-balance-fill" style={{ width: `${max > 0 ? Math.abs(item.amount) / max * 100 : 0}%`, background: item.amount < 0 ? "var(--danger)" : undefined }} /></div>
      </li>)}</ul>
      <p className="finance-chart-note">{note}</p>
    </div>
  );
}
